import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import { notificationService } from '../services/notificationService';
import { Project, ProjectFile, ChatQuery, UploadProgress } from '../types';
import QueryTableList from './QueryTableList';
import CloudIntegrations from './CloudIntegrations';

interface ProjectDetailsModalProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
  onOpenQueryTable?: (project: Project) => void;
  onNavigateToIntegrations?: () => void;
}

const ProjectDetailsModal: React.FC<ProjectDetailsModalProps> = ({ project, isOpen, onClose, onOpenQueryTable, onNavigateToIntegrations }) => {
  const [activeTab, setActiveTab] = useState('queries');
  const [projectFiles, setProjectFiles] = useState<ProjectFile[]>([]);
  const [projectQueries, setProjectQueries] = useState<ChatQuery[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFilesLoading, setIsFilesLoading] = useState(false);
  const [isQueriesLoading, setIsQueriesLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [selectedQuery, setSelectedQuery] = useState<ChatQuery | null>(null);
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [currentResponse, setCurrentResponse] = useState<string>('');
  const [currentSources, setCurrentSources] = useState<any[]>([]);
  const [showCurrentResponse, setShowCurrentResponse] = useState(false);
  const [selectedSource, setSelectedSource] = useState<any>(null);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [passwordPrompt, setPasswordPrompt] = useState<{ file: File; fileName: string } | null>(null);
  const [password, setPassword] = useState('');
  const [isCheckingPassword, setIsCheckingPassword] = useState(false);

  const loadProjectData = useCallback(async () => {
    if (!project) return;

    try {
      setIsFilesLoading(true);
      setIsQueriesLoading(true);

      // Load project files
      const filesData = await apiService.getProjectFiles(project.id);
      setProjectFiles(filesData.files || []);
      setIsFilesLoading(false);

      // Load project queries
      const queriesData = await apiService.getProjectQueries(project.id);
      setProjectQueries(queriesData.queries || []);
      setIsQueriesLoading(false);
    } catch (error) {
      console.error('Error loading project data:', error);
      notificationService.error('Failed to load project data');
      setIsFilesLoading(false);
      setIsQueriesLoading(false);
    }
  }, [project, setProjectFiles, setIsFilesLoading, setProjectQueries, setIsQueriesLoading]);

  useEffect(() => {
    if (isOpen && project) {
      loadProjectData();
    }
  }, [isOpen, project, loadProjectData]);

  const handleSendQuery = async () => {
    if (!chatInput.trim() || !project) return;

    const completedFiles = projectFiles.filter(file => file.status === 'completed');
    if (completedFiles.length === 0) {
      notificationService.warning('No completed documents in this project to query');
      return;
    }

    setIsLoading(true);

    try {
      const documentIds = completedFiles.map(file => file.id);
      const response = await apiService.sendChatQuery({
        message: chatInput,
        document_ids: documentIds,
        context_limit: 8,
        project_id: project.id
      });

      // Display the response immediately with sources
      setCurrentResponse(response.response);
      setCurrentSources(response.sources || []);
      setShowCurrentResponse(true);

      setChatInput('');
      
      // Show option to create structured query table
      const createTableResult = await showCreateTableOption(chatInput, response.response);
      if (createTableResult) {
        // Switch to table tab
        setActiveTab('table');
      }
      
      notificationService.success('Query completed successfully');
      
      // Reload queries
      await loadProjectData();

    } catch (error: any) {
      console.error('Error sending query:', error);
      notificationService.error('Failed to send query');
    } finally {
      setIsLoading(false);
    }
  };

  const getFileIcon = (fileType: string) => {
    switch(fileType?.toLowerCase()) {
      case 'pdf': return 'pdf';
      case 'docx': return 'word';
      case 'pptx': return 'ppt';
      case 'xlsx': return 'excel';
      default: return 'text';
    }
  };


  const handlePasswordSubmit = async () => {
    if (!passwordPrompt || !password.trim() || !project) return;

    setIsCheckingPassword(true);

    try {
      const formData = new FormData();
      formData.append('file', passwordPrompt.file);
      formData.append('password', password);
      formData.append('project_id', project.id.toString());
      formData.append('display_name', passwordPrompt.fileName);

      await apiService.uploadToProjectWithPassword(formData, (newProgress) => {
        setUploadProgress(prev =>
          prev.map(item =>
            item.fileName === passwordPrompt.fileName
              ? { ...item, progress: 80 + (Math.min(newProgress, 80) * 0.2) } // Cap at 99% during password upload
              : item
          )
        );
      });

      setUploadProgress(prev =>
        prev.map(item =>
          item.fileName === passwordPrompt.fileName
            ? { ...item, status: 'processing', progress: 80 }
            : item
        )
      );

      notificationService.info(`⏳ ${passwordPrompt.fileName} uploaded. Processing...`, 3000);

      setPasswordPrompt(null);
      setPassword('');

      await loadProjectData();

      // Clear progress after a delay
      setTimeout(() => {
        setUploadProgress([]);
      }, 3000);

    } catch (error: any) {
      console.error('Password upload error:', error);

      const errorDetail = error.response?.data?.detail || '';

      if (error.response?.status === 401 || (error.response?.status === 400 && errorDetail.includes("Failed to process PDF"))) {
        notificationService.error('Incorrect password. Please try again.');
        setPassword(''); // Clear password field for retry
        // Keep progress at 80% and status as failed with the password error message
        setUploadProgress(prev =>
          prev.map(item =>
            item.fileName === passwordPrompt.fileName
              ? { ...item, progress: 80, status: 'failed', error: 'PDF is password encrypted. Please enter the password.' }
              : item
          )
        );
      } else {
        setUploadProgress(prev =>
          prev.map(item =>
            item.fileName === passwordPrompt.fileName
              ? {
                  ...item,
                  status: 'failed',
                  progress: 0, // Reset progress for other failures
                  error: error.response?.data?.detail || error.message || 'Upload failed'
                }
              : item
          )
        );

        notificationService.error(`❌ Failed to upload ${passwordPrompt.fileName}: ${error.response?.data?.detail || error.message}`);
        setPasswordPrompt(null);
        setPassword('');

        // Clear progress after a delay
        setTimeout(() => {
          setUploadProgress([]);
        }, 3000);
      }
    } finally {
      setIsCheckingPassword(false);
    }
  };

  const handlePasswordCancel = () => {
    if (passwordPrompt) {
      setUploadProgress(prev =>
        prev.map(item =>
          item.fileName === passwordPrompt.fileName
            ? { ...item, status: 'failed', error: 'Upload cancelled - password required' }
            : item
        )
      );

      // Clear progress after a delay
      setTimeout(() => {
        setUploadProgress([]);
      }, 3000);
    }

    setPasswordPrompt(null);
    setPassword('');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !project) return;

    const files = Array.from(e.target.files);
    setIsLoading(true);

    if (e.target) {
      e.target.value = '';
    }

    const initialProgress: UploadProgress[] = files.map(file => ({
      fileName: file.name,
      progress: 0,
      status: 'uploading'
    }));
    setUploadProgress(initialProgress);

    let passwordPromptTriggered = false; // Flag to stop further uploads if password prompt appears

    try {
      for (let i = 0; i < files.length; i++) {
        if (passwordPromptTriggered) break; // Stop if password prompt was triggered

        const file = files[i];

        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('project_id', project.id.toString());
          formData.append('display_name', file.name);

          await apiService.uploadToProject(formData, (currentProgress) => {
            setUploadProgress(prev =>
              prev.map((item, index) =>
                index === i ? { ...item, progress: Math.min(currentProgress, 80) } : item // Cap at 99% during upload
              )
            );
          });

          // After successful upload, set to processing state
          setUploadProgress(prev =>
            prev.map((item, index) =>
              index === i ? { ...item, status: 'processing', progress: 80 } : item
            )
          );

          notificationService.info(`⏳ ${file.name} uploaded. Processing...`);

        } catch (error: any) {
          console.error('Upload error:', error);

          const isPdfPasswordError =
            error.response?.status === 400 &&
            (error.response?.data?.detail?.includes("PDF is password encrypted") ||
             error.response?.data?.detail?.includes("upload-with-password"));

          if (isPdfPasswordError) {
            setPasswordPrompt({ file, fileName: file.name });
            passwordPromptTriggered = true; // Set flag
            setIsLoading(false); // Stop overall uploading state
            // Mark current file as needing password, ensure progress is 80%
            setUploadProgress(prev =>
              prev.map((item, index) =>
                index === i ? {
                  ...item,
                  progress: 80, // Stop at 80% for password prompt
                  status: 'failed', // Use existing 'failed' status
                  error: 'PDF is password encrypted. Please enter the password.'
                } : item
              )
            );
            break; // Exit the loop
          }

          setUploadProgress(prev =>
            prev.map((item, index) =>
              index === i ? {
                ...item,
                status: 'failed',
                error: error.response?.data?.detail || error.message || 'Upload failed'
              } : item
            )
          );

          notificationService.error(`❌ Failed to upload ${file.name}: ${error.response?.data?.detail || error.message}`);
        }
      }

      // Only reload project data if no password prompt was triggered
      if (!passwordPromptTriggered) {
        await loadProjectData();
      }

    } finally {
      // Only set isUploading to false if no password prompt is active
      if (!passwordPromptTriggered) {
        setIsLoading(false);
        setTimeout(() => {
          setUploadProgress([]);
        }, 3000);
      }

      if (e.target) {
        e.target.value = '';
      }
      const fileInput = document.getElementById('project-file-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };

  const showCreateTableOption = async (queryText: string, response: string): Promise<boolean> => {
    return new Promise((resolve) => {
      // Show a notification with option to create table
      const notification = document.createElement('div');
      notification.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 0.75rem;
        padding: 1.5rem;
        box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
        z-index: 9999;
        max-width: 400px;
        width: 90%;
      `;
      
      notification.innerHTML = `
        <div style="text-align: center;">
          <div style="background: #ddd6fe; border-radius: 50%; width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem;">
            <i class="bi bi-table" style="color: #7c3aed; font-size: 1.5rem;"></i>
          </div>
          <h6 style="color: #1e293b; margin: 0 0 0.5rem 0; font-weight: 600;">Create Query Table?</h6>
          <p style="color: #64748b; margin: 0 0 1.5rem 0; font-size: 0.9rem;">
            Turn this query into a structured table to analyze multiple documents with column-based queries.
          </p>
          <div style="display: flex; gap: 0.75rem; justify-content: center;">
            <button id="cancel-table" style="background: white; border: 1px solid #d1d5db; border-radius: 0.375rem; padding: 0.5rem 1rem; color: #64748b; cursor: pointer;">
              Not now
            </button>
            <button id="create-table" style="background: #1e293b; border: none; border-radius: 0.375rem; padding: 0.5rem 1rem; color: white; cursor: pointer; font-weight: 500;">
              Create Table
            </button>
          </div>
        </div>
      `;
      
      document.body.appendChild(notification);
      
      const createBtn = notification.querySelector('#create-table');
      const cancelBtn = notification.querySelector('#cancel-table');
      
      createBtn?.addEventListener('click', async () => {
        try {
          // Create structured query
          await apiService.createStructuredQuery(project!.id, {
            name: queryText.length > 50 ? queryText.substring(0, 47) + '...' : queryText,
            description: `Created from query: ${queryText}`,
            original_query: queryText,
            query_type: 'analysis',
            columns: [] // Start with no columns, user will add them
          });
          
          notificationService.success('Query table created successfully!');
          document.body.removeChild(notification);
          resolve(true);
        } catch (error) {
          notificationService.error('Failed to create query table');
          document.body.removeChild(notification);
          resolve(false);
        }
      });
      
      cancelBtn?.addEventListener('click', () => {
        document.body.removeChild(notification);
        resolve(false);
      });
      
      // Auto-close after 10 seconds
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
          resolve(false);
        }
      }, 10000);
    });
  };

  const formatAssistantResponse = (message: string, sources?: any[]) => {
    // Basic formatting for assistant responses
    let formatted = message;
    
    // Debug: Log the message and sources to see what we're working with
    console.log('Formatting message:', message);
    console.log('Available sources:', sources);
    
    // Format bold text
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Clean up any existing HTML attributes that might be showing as text - more comprehensive cleaning
    formatted = formatted.replace(/onmouseover="[^"]*"/gi, '');
    formatted = formatted.replace(/onmouseout="[^"]*"/gi, '');
    formatted = formatted.replace(/style="[^"]*"/gi, '');
    formatted = formatted.replace(/class="[^"]*"/gi, '');
    formatted = formatted.replace(/id="[^"]*"/gi, '');
    
    // Remove any remaining HTML-like attributes that are showing as text (more aggressive)
    formatted = formatted.replace(/\s*(onmouseover|onmouseout|style|class|id)="[^"]*"/gi, '');
    
    // Remove any stray HTML-like content that might be embedded
    formatted = formatted.replace(/\s*onmouseover[^>]*>/gi, '');
    formatted = formatted.replace(/\s*onmouseout[^>]*>/gi, '');
    
    // Clean up any malformed HTML tags or attributes
    formatted = formatted.replace(/<[^>]*onmouseover[^>]*>/gi, '');
    formatted = formatted.replace(/<[^>]*onmouseout[^>]*>/gi, '');
    
    // Remove raw source metadata sections that are showing as text
    formatted = formatted.replace(/### DOCUMENT SOURCES[\s\S]*?(?=\n\n|$)/gi, '');
    formatted = formatted.replace(/- Source \d+ \[[^\]]+\]/gi, '');
    
    // If we have sources, add clean clickable inline citations
    if (sources && sources.length > 0) {
      // Try multiple patterns for source citations
      const sourcePatterns = [
        /\(Source: ([^)]+)\)/g,
        /\[Source: ([^\]]+)\]/g,
        /Source: ([^.]+)\./g,
        /\(([^)]*Document[^)]*)\)/g,
        /\(([^)]*Page[^)]*)\)/g
      ];
      
      let foundSources = false;
      
      // Try each pattern
      for (const pattern of sourcePatterns) {
        if (pattern.test(formatted)) {
          foundSources = true;
          formatted = formatted.replace(pattern, (match, sourceText) => {
            console.log('Found source match:', match, sourceText);
            
            // Try to find corresponding source object
            let matchedSource: any = null;
            
            // Try different matching strategies
            for (const source of sources) {
              if (source.metadata) {
                // Match by document ID
                if (sourceText.includes(source.metadata.document_id?.toString())) {
                  matchedSource = source;
                  break;
                }
                // Match by filename
                if (source.metadata.filename && sourceText.includes(source.metadata.filename)) {
                  matchedSource = source;
                  break;
                }
                // Match by page number
                if (sourceText.includes(`Page ${source.metadata.page}`)) {
                  matchedSource = source;
                  break;
                }
              }
            }
            
            // If no specific match, use first source as fallback
            if (!matchedSource && sources.length > 0) {
              matchedSource = sources[0];
            }
            
            if (matchedSource) {
              const sourceId = `source-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
              
              // Store source data for click handler
              setTimeout(() => {
                if (typeof document !== 'undefined') {
                  const element = document.getElementById(sourceId);
                  if (element) {
                    element.addEventListener('click', () => {
                      setSelectedSource(matchedSource);
                      setShowSourceModal(true);
                    });
                  
                    // Add hover effects
                    element.addEventListener('mouseenter', () => {
                      element.style.transform = 'translateY(-1px)';
                      element.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.4)';
                      element.style.background = 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)';
                    });
                  
                    element.addEventListener('mouseleave', () => {
                      element.style.transform = 'translateY(0px)';
                      element.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.3)';
                      element.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
                    });
                  }
                }
              }, 100);
              
              return `<span id="${sourceId}" class="inline-source-link" style="
                color: #ffffff; 
                cursor: pointer; 
                text-decoration: none;
                font-size: 0.8rem;
                font-weight: 600;
                padding: 4px 8px;
                border-radius: 6px;
                background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                border: none;
                margin: 0 3px;
                display: inline-block;
                transition: all 0.3s ease;
                box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
                text-transform: uppercase;
                letter-spacing: 0.5px;
              ">🔗 SOURCE</span>`;
            }
            
            // Fallback styling
            return `<span style="
              color: #64748b; 
              font-size: 0.875rem;
              font-style: italic;
              padding: 2px 6px;
              border-radius: 4px;
              background: rgba(100, 116, 139, 0.1);
              border: 1px solid rgba(100, 116, 139, 0.2);
              margin: 0 2px;
              display: inline-block;
            ">📄 ${sourceText}</span>`;
          });
          break; // Stop after first successful pattern match
        }
      }
      
      // If no source citations found in text, add them at the end
      if (!foundSources) {
        console.log('No source citations found in text, adding at end');
        const sourceLinks = sources.map((source, index) => {
          const docId = source.metadata?.document_id;
          const document = projectFiles.find(file => file.id === docId);
          const docName = document ? (document.display_name || document.original_filename) : (source.metadata?.filename || `Document ${docId}`);
          const sourceId = `source-end-${Date.now()}-${index}`;
          
          // Store source data for click handler
          setTimeout(() => {
            if (typeof window !== 'undefined' && window.document) {
              const element = window.document.getElementById(sourceId);
              if (element) {
                element.addEventListener('click', () => {
                  setSelectedSource(source);
                  setShowSourceModal(true);
                });
                
                // Add hover effects
                element.addEventListener('mouseenter', () => {
                  element.style.transform = 'translateY(-1px)';
                  element.style.boxShadow = '0 4px 8px rgba(102, 126, 234, 0.4)';
                });
                
                element.addEventListener('mouseleave', () => {
                  element.style.transform = 'translateY(0px)';
                  element.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.3)';
                });
              }
            }
          }, 100);
          
          return `<span id="${sourceId}" class="inline-source-link" style="
            color: #ffffff; 
            cursor: pointer; 
            text-decoration: none;
            font-size: 0.75rem;
            font-weight: 600;
            padding: 3px 8px;
            border-radius: 6px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border: none;
            margin: 0 4px;
            display: inline-block;
            transition: all 0.3s ease;
            box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
            text-transform: uppercase;
            letter-spacing: 0.3px;
          ">📄 ${docName.split('.')[0]}</span>`;
        }).join(' ');
        
        formatted += `<br><br><small style="color: #64748b; font-weight: 500;">Sources: ${sourceLinks}</small>`;
      }
    }
    
    // Format line breaks
    formatted = formatted.replace(/\n\n/g, '</p><p>');
    formatted = formatted.replace(/\n/g, '<br>');
    
    // Wrap in paragraphs
    if (!formatted.includes('<p>')) {
      formatted = `<p>${formatted}</p>`;
    }
    
    return formatted;
  };

  const formatSourceContent = (content: string): string => {
    if (!content) return 'No content available';
    
    // Clean up HTML attributes and malformed content first
    let cleaned = content;
    
    // Remove all HTML attributes that are showing as text
    cleaned = cleaned.replace(/onmouseover="[^"]*"/gi, '');
    cleaned = cleaned.replace(/onmouseout="[^"]*"/gi, '');
    cleaned = cleaned.replace(/style="[^"]*"/gi, '');
    cleaned = cleaned.replace(/class="[^"]*"/gi, '');
    cleaned = cleaned.replace(/id="[^"]*"/gi, '');
    
    // Remove CSS properties that are showing as text
    cleaned = cleaned.replace(/\b(color|background|padding|margin|border|font-weight|font-size|width|height|display|overflow|border-radius|box-shadow|border-collapse)\s*[#\w\s\-(),.%;]+/gi, '');
    
    // Clean up extra whitespace and line breaks
    cleaned = cleaned.replace(/\s+/g, ' ').trim();
    
    // Format basic text styling
    cleaned = cleaned.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    cleaned = cleaned.replace(/\n\n/g, '</p><p>');
    cleaned = cleaned.replace(/\n/g, '<br>');
    
    // Wrap in paragraphs if not already wrapped
    if (!cleaned.includes('<p>')) {
      cleaned = `<p>${cleaned}</p>`;
    }
    
    return cleaned;
  };


  if (!isOpen || !project) return null;

  const Loader = ({ message }: { message: string }) => (
    <div style={{ textAlign: 'center', padding: '3rem 0' }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p style={{ color: 'var(--text-secondary)', marginTop: '1rem', fontSize: '1rem' }}>{message}</p>
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div
        style={{
          background: 'var(--bg-color)',
          borderRadius: 'var(--radius-lg)',
          width: '95%',
          maxWidth: '1400px',
          maxHeight: '95vh',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-xl)'
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            background: 'var(--bg-color)',
            borderBottom: '1px solid var(--border-color)',
            padding: '1.5rem 2rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ marginRight: '1rem' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 600 }}>
                {project.name}
              </h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginTop: '0.5rem'
              }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  {project.file_count} files ({(project.total_size_bytes / (1024 * 1024)).toFixed(2)} MB)
                </span>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  {project.query_count} queries
                </span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              style={{
                background: 'var(--bg-color)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                padding: '0.75rem 1rem',
                color: 'var(--text-secondary)',
                fontWeight: 500,
                fontSize: '0.9rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <i className="bi bi-share"></i>
              Share
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-secondary)',
                fontSize: '1.25rem',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: 'var(--radius-sm)'
              }}
            >
              <i className="bi bi-x"></i>
            </button>
          </div>
        </div>

        {/* Chat Input */}
        <div
          style={{
            background: 'var(--surface-color)',
            padding: '1.5rem 2rem',
            borderBottom: '1px solid var(--border-color)'
          }}
        >
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Ask AnCore about the files in your project..."
              disabled={isLoading}
              style={{
                flex: 1,
                background: 'var(--bg-color)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                padding: '0.75rem 1rem',
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
                outline: 'none'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendQuery();
                }
              }}
            />
            <button
              onClick={handleSendQuery}
              disabled={isLoading || !chatInput.trim()}
              style={{
                background: 'var(--primary-color)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                padding: '0.75rem 1.5rem',
                color: 'white',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              {isLoading ? (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                'Ask AnCore'
              )}
            </button>
          </div>
        </div>

        {/* Current Response Display */}
        {showCurrentResponse && currentResponse && (
          <div
            style={{
              background: 'var(--bg-color)',
              padding: '1.5rem 2rem',
              borderBottom: '1px solid var(--border-color)'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <h6 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <i className="bi bi-robot" style={{ marginRight: '0.5rem', color: 'var(--primary-color)' }}></i>
                AnCore Response
              </h6>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  borderRadius: 'var(--radius-sm)'
                }}
                onClick={() => setShowCurrentResponse(false)}
                title="Hide response"
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
            <div
              style={{
                background: 'var(--surface-color)',
                padding: '1.5rem',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-color)',
                fontSize: '0.9rem',
                lineHeight: '1.6',
                color: 'var(--text-primary)',
                maxHeight: '400px',
                overflowY: 'auto'
              }}
              dangerouslySetInnerHTML={{
                __html: formatAssistantResponse(currentResponse, currentSources)
              }}
            />
          </div>
        )}

        {/* Tabs */}
        <div
          style={{
            background: 'var(--bg-color)',
            padding: '0 2rem',
            borderBottom: '1px solid var(--border-color)'
          }}
        >
          <div style={{ display: 'flex', gap: '0' }}>
            {['table', 'integrations', 'queries', 'files'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-secondary)',
                  padding: '1rem 1.5rem',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  fontWeight: activeTab === tab ? 600 : 500,
                  fontSize: '0.9rem',
                  borderBottom: activeTab === tab ? '2px solid var(--primary-color)' : '2px solid transparent',
                  transition: 'all var(--transition-normal)'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab) {
                    e.currentTarget.style.color = 'var(--text-primary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab) {
                    e.currentTarget.style.color = 'var(--text-secondary)';
                  }
                }}
              >
                {tab === 'table' ? 'Query Table' :
                 tab === 'integrations' ? 'Cloud Storage' : tab}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div
          style={{
            background: 'var(--bg-color)',
            padding: '2rem',
            height: '500px',
            overflowY: 'auto',
            position: 'relative'
          }}
        >
          {activeTab === 'chat' && (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem'
              }}>
                <h5 style={{ color: 'var(--text-primary)', margin: 0, fontWeight: 600 }}>Recent queries</h5>
                <button style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--primary-color)',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}>
                  View all →
                </button>
              </div>
              {isQueriesLoading ? (
                <Loader message="Loading queries..." />
              ) : projectQueries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                  <i className="bi bi-chat-dots" style={{ fontSize: '2.5rem', color: 'var(--text-muted)' }}></i>
                  <p style={{ color: 'var(--text-secondary)', marginTop: '1rem', fontSize: '1rem' }}>No queries yet</p>
                  <small style={{ color: 'var(--text-muted)' }}>Ask AnCore a question to get started</small>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {projectQueries.slice(0, 5).map((query, index) => (
                    <div
                      key={query.id}
                      style={{
                        background: 'var(--surface-color)',
                        border: '1px solid var(--border-color)',
                        borderRadius: 'var(--radius-md)',
                        padding: '1.25rem',
                        transition: 'all var(--transition-normal)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--card-color)';
                        e.currentTarget.style.borderColor = 'var(--text-muted)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--surface-color)';
                        e.currentTarget.style.borderColor = 'var(--border-color)';
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '0.75rem'
                      }}>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.95rem', flex: 1 }}>
                          {query.query_text}
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginLeft: '1rem'
                        }}>
                          <span style={{
                            background: '#f1f5f9',
                            color: '#64748b',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}>
                            {query.query_type === 'chat' ? 'Ask query' : 'Review query'}
                          </span>
                          <small style={{ color: '#9ca3af', fontSize: '0.8rem' }}>
                            {new Date(query.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}
                          </small>
                        </div>
                      </div>
                      {query.response_preview && (
                        <div
                          style={{
                            color: '#64748b',
                            fontSize: '0.85rem',
                            lineHeight: 1.5
                          }}
                          dangerouslySetInnerHTML={{
                            __html: formatAssistantResponse(query.response_preview)
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'files' && (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem'
              }}>
                <h5 style={{ color: 'var(--text-primary)', margin: 0, fontWeight: 600 }}>Project files</h5>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button style={{
                    background: 'var(--bg-color)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.5rem',
                    padding: '0.5rem 1rem',
                    color: 'var(--text-secondary)',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <i className="bi bi-folder-plus"></i>
                    Create folder
                  </button>
                  <button
                    onClick={() => document.getElementById('project-file-input')?.click()}
                    style={{
                      background: 'var(--text-primary)',
                      border: 'none',
                      borderRadius: '0.5rem',
                      padding: '0.5rem 1rem',
                      color: 'var(--bg-color)',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <i className="bi bi-upload"></i>
                    Upload files
                  </button>
                </div>
              </div>

              {/* Upload Progress */}
              {uploadProgress.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h6 style={{ marginBottom: '1rem', color: 'var(--text-primary)', fontWeight: 600 }}>Upload Progress</h6>
                  {uploadProgress.map((item, index) => (
                    <div key={index} style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <i className={`bi ${item.status === 'completed' ? 'bi-check-circle' : (item.status === 'failed' && item.error?.includes('password encrypted')) ? 'bi-lock' : item.status === 'failed' ? 'bi-x-circle' : item.status === 'processing' ? 'spinner-border spinner-border-sm' : 'bi-arrow-up-circle'}`} style={{
                            marginRight: '0.5rem',
                            color: item.status === 'completed' ? '#10b981' : (item.status === 'failed' && item.error?.includes('password encrypted')) ? '#f59e0b' : item.status === 'failed' ? '#ef4444' : item.status === 'processing' ? 'var(--primary-color)' : '#1e293b'
                          }}></i>
                          <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{item.fileName}</span>
                        </div>
                        <small style={{ color: 'var(--text-secondary)' }}>
                          {item.status === 'uploading' || item.status === 'processing' || (item.status === 'failed' && item.error?.includes('password encrypted')) ? `${Math.round(item.progress)}%` : item.status}
                        </small>
                      </div>
                      
                      <div style={{
                        height: '6px',
                        background: 'var(--bg-secondary)',
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <div
                          style={{
                            width: `${item.progress}%`,
                            height: '100%',
                            background: item.status === 'completed' ? 'var(--success-color)' : (item.status === 'failed' && item.error?.includes('password encrypted')) ? 'var(--warning-color)' : item.status === 'failed' ? 'var(--danger-color)' : item.status === 'processing' ? 'var(--primary-color)' : 'var(--primary-color)',
                            transition: 'width 0.3s ease'
                          }}
                        ></div>
                      </div>
                      
                      {item.error && (
                        <small style={{ color: 'var(--danger-color)', marginTop: '0.25rem', display: 'block' }}>
                          <i className="bi bi-exclamation-triangle" style={{ marginRight: '0.25rem' }}></i>
                          {item.error}
                        </small>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {isFilesLoading ? (
                <Loader message="Loading files..." />
              ) : projectFiles.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                  <i className="bi bi-inbox" style={{ fontSize: '2.5rem', color: 'var(--border-color)' }}></i>
                  <p style={{ color: 'var(--text-secondary)', marginTop: '1rem', fontSize: '1rem' }}>No files in this project</p>
                  <small style={{ color: 'var(--text-muted)' }}>Upload files to get started</small>
                </div>
              ) : (
                <div style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.5rem',
                  overflow: 'hidden'
                }}>
                  {/* Table Header */}
                  <div style={{
                    background: 'var(--surface-color)',
                    padding: '0.75rem 1rem',
                    borderBottom: '1px solid var(--border-color)',
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
                    gap: '1rem',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)'
                  }}>
                    <div>Name</div>
                    <div>Document type</div>
                    <div>Uploaded</div>
                    <div>Kind</div>
                    <div>Size</div>
                    <div>Actions</div>
                  </div>
                  
                  {/* Table Rows */}
                  {projectFiles.map((file, index) => (
                    <div
                      key={file.id}
                      style={{
                        padding: '1rem',
                        borderBottom: index < projectFiles.length - 1 ? '1px solid var(--border-color)' : 'none',
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                        gap: '1rem',
                        alignItems: 'center',
                        fontSize: '0.9rem',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--surface-color)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <i className={`bi bi-file-earmark-${getFileIcon(file.file_type)}`} style={{
                          fontSize: '1.25rem',
                          color: 'var(--primary-color)',
                          marginRight: '0.75rem'
                        }}></i>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                          {file.display_name}
                        </span>
                      </div>
                      <div style={{ color: 'var(--text-secondary)' }}>
                        {file.file_type.toUpperCase()}
                      </div>
                      <div style={{ color: 'var(--text-secondary)' }}>
                        {new Date(file.uploaded_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                      <div>
                        <span style={{
                          background: file.status === 'completed' ? 'var(--success-bg)' : 'var(--warning-bg)',
                          color: file.status === 'completed' ? 'var(--success-color)' : 'var(--warning-color)',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          fontWeight: 500
                        }}>
                          {file.status}
                        </span>
                      </div>
                      <div style={{ color: 'var(--text-secondary)' }}>
                        {(file.file_size / (1024 * 1024)).toFixed(2)} MB
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'table' && onOpenQueryTable && (
            isQueriesLoading ? (
              <Loader message="Loading query tables..." />
            ) : (
              <QueryTableList project={project} onOpenQueryTable={onOpenQueryTable} />
            )
          )}

          {activeTab === 'integrations' && (
            isFilesLoading ? (
              <Loader message="Loading integrations..." />
            ) : (
              <CloudIntegrations
                project={project}
                onFilesImported={loadProjectData}
                onNavigateToIntegrations={onNavigateToIntegrations}
              />
            )
          )}

          {activeTab === 'queries' && (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem'
              }}>
                <h5 style={{ color: 'var(--text-primary)', margin: 0, fontWeight: 600 }}>Query History</h5>
              </div>
              {isQueriesLoading ? (
                <Loader message="Loading query history..." />
              ) : projectQueries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                  <i className="bi bi-chat-quote" style={{ fontSize: '2.5rem', color: 'var(--border-color)' }}></i>
                  <p style={{ color: 'var(--text-secondary)', marginTop: '1rem', fontSize: '1rem' }}>No queries yet</p>
                  <small style={{ color: 'var(--text-muted)' }}>Ask questions to build your query history</small>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {projectQueries.map((query) => (
                    <div
                      key={query.id}
                      style={{
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '0.5rem',
                        padding: '1.25rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => {
                        setSelectedQuery(query);
                        setShowQueryModal(true);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--surface-color)';
                        e.currentTarget.style.borderColor = 'var(--text-muted)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgb(0 0 0 / 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-secondary)';
                        e.currentTarget.style.borderColor = 'var(--border-color)';
                        e.currentTarget.style.transform = 'translateY(0px)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{
                        color: 'var(--text-primary)',
                        fontWeight: 500,
                        marginBottom: '0.5rem',
                        fontSize: '0.95rem'
                      }}>
                        {query.query_text}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <small style={{ color: 'var(--text-secondary)' }}>
                            {new Date(query.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </small>
                          <span style={{
                            background: 'var(--surface-color)',
                            color: 'var(--text-secondary)',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}>
                            {query.query_type}
                          </span>
                        </div>
                        <div style={{
                          color: 'var(--primary-color)',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}>
                          <i className="bi bi-eye"></i>
                          View Response
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Hidden File Input */}
        <input
          id="project-file-input"
          type="file"
          multiple
          accept=".pdf,.docx,.pptx,.xlsx"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
        />
      </div>

      {/* Source Modal */}
      {showSourceModal && selectedSource && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'var(--bg-color)',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 10px 10px -5px rgb(0 0 0 / 0.04)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid var(--border-color)'
            }}>
              <h5 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 600 }}>
                <i className="bi bi-file-text" style={{ marginRight: '0.5rem' }}></i>
                Source Document
              </h5>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                  padding: '0.25rem'
                }}
                onClick={() => setShowSourceModal(false)}
              >
                <i className="bi bi-x"></i>
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{
                background: 'var(--bg-secondary)',
                padding: '1rem',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <i className="bi bi-file-earmark"></i>
                    <strong>Document:</strong> {selectedSource.metadata?.filename || 'Unknown Document'}
                  </div>
                  {selectedSource.metadata?.page && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <i className="bi bi-file-text"></i>
                      <strong>Page:</strong> {selectedSource.metadata.page}
                    </div>
                  )}
                  {selectedSource.metadata?.section && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <i className="bi bi-bookmark"></i>
                      <strong>Section:</strong> {selectedSource.metadata.section}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <strong style={{ color: '#1e293b', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                Source Content:
              </strong>
              <div
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '1.5rem',
                  borderRadius: '0.5rem',
                  border: '1px solid var(--border-color)',
                  fontSize: '0.9rem',
                  lineHeight: '1.6',
                  color: 'var(--text-primary)',
                  maxHeight: '400px',
                  overflowY: 'auto'
                }}
                dangerouslySetInnerHTML={{
                  __html: formatSourceContent(selectedSource.page_content || 'No content available')
                }}
              />
            </div>

            <div style={{
              marginTop: '1.5rem',
              paddingTop: '1rem',
              borderTop: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                style={{
                  padding: '0.5rem 1rem',
                  background: 'var(--text-primary)',
                  color: 'var(--bg-color)',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
                onClick={() => setShowSourceModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {passwordPrompt && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 4000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'var(--bg-color)',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 10px 10px -5px rgb(0 0 0 / 0.04)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'var(--warning-bg)',
                color: 'var(--warning-color)',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1rem'
              }}>
                <i className="bi bi-lock" style={{ fontSize: '1.5rem' }}></i>
              </div>
              <h5 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 600, marginBottom: '0.5rem' }}>
                Password Protected PDF
              </h5>
              <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.875rem' }}>
                This PDF requires a password to access. Please enter the password to continue.
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--text-primary)',
                marginBottom: '0.5rem',
                display: 'block'
              }}>
                File: {passwordPrompt.fileName}
              </label>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--text-primary)',
                marginBottom: '0.5rem',
                display: 'block'
              }}>
                Password:
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                placeholder="Enter PDF password"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                  background: 'var(--bg-color)',
                  color: 'var(--text-primary)'
                }}
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={handlePasswordCancel}
                disabled={isCheckingPassword}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'var(--bg-color)',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.5rem',
                  cursor: isCheckingPassword ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  opacity: isCheckingPassword ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordSubmit}
                disabled={!password.trim() || isCheckingPassword}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: !password.trim() || isCheckingPassword ? 'var(--border-color)' : 'var(--text-primary)',
                  color: !password.trim() || isCheckingPassword ? 'var(--text-secondary)' : 'var(--bg-color)',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: !password.trim() || isCheckingPassword ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {isCheckingPassword && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid currentColor',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                )}
                {isCheckingPassword ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Query Response Modal */}
      {showQueryModal && selectedQuery && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'var(--bg-color)',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 10px 10px -5px rgb(0 0 0 / 0.04)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid var(--border-color)'
            }}>
              <h5 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 600 }}>
                <i className="bi bi-chat-quote" style={{ marginRight: '0.5rem' }}></i>
                Query Response
              </h5>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                  padding: '0.25rem'
                }}
                onClick={() => setShowQueryModal(false)}
              >
                <i className="bi bi-x"></i>
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{
                background: 'var(--bg-secondary)',
                padding: '1rem',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <strong style={{ color: 'var(--text-primary)', fontSize: '0.875rem' }}>Question:</strong>
                  <div style={{ color: 'var(--text-primary)', fontSize: '0.9rem', marginTop: '0.5rem', fontWeight: 500 }}>
                    {selectedQuery.query_text}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <i className="bi bi-calendar3"></i>
                    {new Date(selectedQuery.created_at).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <i className="bi bi-tag"></i>
                    {selectedQuery.query_type}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <strong style={{ color: 'var(--text-primary)', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                AnCore Response:
              </strong>
              <div
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '1.5rem',
                  borderRadius: '0.5rem',
                  border: '1px solid var(--border-color)',
                  fontSize: '0.9rem',
                  lineHeight: '1.6',
                  color: 'var(--text-primary)'
                }}
                dangerouslySetInnerHTML={{
                  __html: selectedQuery.response_text ?
                    formatAssistantResponse(selectedQuery.response_text)
                    : 'No response available'
                }}
              />
            </div>

            <div style={{
              marginTop: '1.5rem',
              paddingTop: '1rem',
              borderTop: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                style={{
                  padding: '0.5rem 1rem',
                  background: 'var(--text-primary)',
                  color: 'var(--bg-color)',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
                onClick={() => setShowQueryModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetailsModal;
